import React from 'react'
import { useParams } from 'react-router-dom'
import { useAppSelector } from 'reduxTyped'
import { Loader, Skeleton, l } from '@manychat/manyui'

import steps from 'apps/templates/constants/TemplateInstallationSteps'
import * as appSelectors from 'common/core/selectors/appSelectors'
import { CircleAvatar } from 'components/CircleAvatar'
import FancyBreadcrumbs from 'components/FancyBreadcrumbs'
import { ManyChatLogo } from 'components/ManyChatLogo/ManyChatLogo'
import { useVisitorJs } from 'utils/services/visitor/useVisitorJs'

import Footer from './Footer'

import cm from './InstallationWizardLayout.module.css'

const stepStateMap = {
  [steps.STEP1]: 0,
  [steps.STEP2]: 1,
  [steps.STEP3]: 2,
  [steps.STEP4]: 3,
}

interface InstallationWizardLayoutProps {
  step: string
  children: React.ReactNode
  className?: string
  onInstallTemplate: () => void
  isInstallingComplete: boolean
  isInstalling: boolean
  placeholder?: React.ReactNode
  template: {
    isPro: boolean
    templateName?: string
    author: {
      name?: string
      avatar?: string
    }
    isReady: boolean
    isAi: boolean
  }
  error?: {
    errors: string[]
  }
  fetchingInstalledTemplate: boolean
  installationId: React.ComponentProps<typeof Footer>['installationId']
  isConfigurationPage: boolean
  onChangeStep: React.ComponentProps<typeof Footer>['onChangeStep']
  onSkipWizard: React.ComponentProps<typeof Footer>['onSkipWizard']
}

const InstallationWizardLayout = (props: InstallationWizardLayoutProps) => {
  useVisitorJs()
  const { template_hash: templateHash } = useParams()
  const isSignedIn = Boolean(useAppSelector(appSelectors.getUserId))

  const {
    step,
    children,
    placeholder = <Loader center />,
    template,
    error,
    isInstalling,
    fetchingInstalledTemplate,
    installationId,
    isConfigurationPage,
    onInstallTemplate,
    onChangeStep,
    onSkipWizard,
  } = props

  const { isReady, author, templateName, isPro, isAi } = template

  const isSettingsStep = step === steps.STEP4

  const installationLinkIsExpired = error?.errors?.includes?.('Installation link is invalid')
  const expirationErrorText =
    error?.errors?.[0] || l.translate('Template installation link is invalid')

  const hasAuthorInfo = author && (Boolean(author.name) || Boolean(author.avatar))

  const TITLES = {
    first: l.translate('Review'),
    second: l.translate('Choose Page'),
    third: l.translate('Finalize'),
  }

  return (
    <div className={cm.wrapper}>
      <div className={'d-flex p-a row align-center'}>
        <div className="col-4">
          <ManyChatLogo />
        </div>
        <div className="col-4 text-center">
          {isSettingsStep ? l.translate('Template Settings') : l.translate('Template Installation')}
        </div>
      </div>
      {isReady ? (
        <>
          <div className="m-b text-center">
            <h1 className={cm.title}>
              {templateName}
              {isPro && <span className="pro-label lg m-l-xs" />}
              {isPro && isAi && <span className="ai-label lg m-l-xxs" />}
            </h1>
          </div>
          {hasAuthorInfo && (
            <div className="m-b-md row middle center">
              <CircleAvatar className={'m-r-xs'} size={24} url={author.avatar} />
              {Boolean(author.name) && <div>by {author.name}</div>}
            </div>
          )}
        </>
      ) : (
        <div>
          <Skeleton.Text height={40} className="m-b col-3 m-x-auto" />
          <div className="m-b-md row middle center">
            <Skeleton.Avatar size={24} className="m-r-xs" />
            by <Skeleton.Text className="m-l-xs" />
          </div>
        </div>
      )}

      {!isSettingsStep && (
        <FancyBreadcrumbs
          className={'col-10 sm-col-4 m-b-lg'}
          titles={TITLES}
          currentStep={stepStateMap[step]}
        />
      )}

      {isReady && <div className="sm-p-x-0 p-x">{children}</div>}
      {!isReady && (
        <>
          {error ? (
            <h1 className="text-danger m-y-xl text-center">
              {installationLinkIsExpired ? expirationErrorText : l.translate('Template not found')}
            </h1>
          ) : (
            <span>{placeholder}</span>
          )}
        </>
      )}

      <Footer
        step={step}
        isFetching={isInstalling || fetchingInstalledTemplate || !isReady || Boolean(error)}
        onInstallTemplate={onInstallTemplate}
        onSkipWizard={onSkipWizard}
        onChangeStep={onChangeStep}
        templateHash={templateHash}
        isSignedIn={isSignedIn}
        installationId={installationId}
        isConfigurationPage={isConfigurationPage}
      />
    </div>
  )
}

export default InstallationWizardLayout
